import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Typography, Grid, Box, Container, Card, CardHeader, Divider, CardContent, Hidden, IconButton, Toolbar, Tooltip, Checkbox } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotifyIcon from '@mui/icons-material/Email';
import { connect } from 'react-redux';
import {
  Truck as TruckIcon,
  Edit as EditIcon
} from 'react-feather';
import moment from 'moment';
import ConfirmButton from '../components/ConfirmButton';
import GlobalSearch from '../components/GlobalSearch';
import FilterBy from '../components/FilterBy';
import Table from '../components/Table';
import MobileDataList from '../components/MobileDataList';
import MultiRowActionButton from '../components/MultiRowActionButton';
import AppConstants from '../constants/AppConstants';
import { refreshDataWithDateRange, searchByTextOngoing, searchByWarningOngoing, changePageOngoing, changeSortModelOngoing, changeFilterByColumnOngoing } from '../store/actions/shiftActions';
import { RootState } from '../store/store';
import FormattedShift from '../models/FormattedShift';
import { formatDateToCalendarTime, formatDateToLongDate, formatDateToOnlyTime, formatErrorMessage, formatSuccessMessage } from '../utils/common';
import ShiftsService from '../services/ShiftsService';
import { hide, show } from '../store/actions/busyIndicatorActions';
import {
  showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts
} from '../store/actions/alertActions';

interface Props {
  fetchAllRows: () => void,
  visibleRows: Array<FormattedShift>;
  totalRowCount: number;
  currentPage: number;
  searchedText: string;
  sortModel: any;
  selectedWarning: string;
  filterByText: any;
  filterByWarning: any;
  filterByCol: any;
  onPageChange: any;
  onSort: any;
  onChangeFilterByColumn: any;
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
}

const VehiclesReturns: React.FC<Props> = ({
  fetchAllRows,
  visibleRows,
  totalRowCount,
  currentPage,
  searchedText,
  sortModel,
  selectedWarning,
  filterByText,
  filterByWarning,
  filterByCol,
  onPageChange,
  onSort,
  onChangeFilterByColumn,
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator
}) => {
  const navigate = useNavigate();
  const [selectedShifts, setSelectedShifts] = useState<number[]>([]);

  const handleSelectionModelChange = (selectionModel: number[]) => {
    setSelectedShifts(selectionModel);
  };

  // Select or deselect all visible shifts
  const handleSelectAllShifts = (checked: boolean) => {
    if (checked) {
      setSelectedShifts(visibleRows.map((row) => row.id));
    } else {
      setSelectedShifts([]);
    }
  };

  // Notify all selected drivers (loop)
  const notifySelectedDriversToUpdateReturnTime = () => {
    if (selectedShifts.length === 0) return;

    showBusyIndicator();
    clearAllAlerts();

    // Loop through selected shifts and call notifyDriverToUpdateReturnTime for each
    const promises = selectedShifts.map((id) =>
      ShiftsService.notifyDriverReturnTime(id));

    Promise.allSettled(promises)
      .then((results) => {
        const successCount = results.filter((result) => result.status === 'fulfilled').length;
        const failureCount = results.filter((result) => result.status === 'rejected').length;

        if (successCount > 0) {
          showAlert(formatSuccessMessage(`Notification sent to ${successCount} driver(s) to update truck return time`));
        }
        if (failureCount > 0) {
          showAlert(formatErrorMessage(`Failed to notify ${failureCount} driver(s)`));
        }

        setSelectedShifts([]);
      })
      .catch((err) => {
        showAlert(formatErrorMessage('An error occurred while sending notifications'));
      })
      .finally(() => {
        hideBusyIndicator();
      });
  };

  const notifyDriverToUpdateReturnTime = (id: number) => {
    showBusyIndicator();
    clearAllAlerts();
    ShiftsService.notifyDriverReturnTime(id)
      .then(() => {
        showAlert(formatSuccessMessage('Notification sent to Driver to update return time'));
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
  };

  const columns: Array<any> = [
    {
      field: 'scheduledEndUTC',
      headerName: 'Shift Scheduled End',
      flex: 1,
      type: 'date',
      filterable: false,
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>{value && formatDateToLongDate(value, true)}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'estimatedVehicleReturnLocal',
      headerName: 'Est. Vehicle Return',
      flex: 1,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Estimated vehicle return time (local)">
          <span style={{ fontWeight: '600' }}>Est. Truck Return</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || 'Not set'}>
            <span>
              {value ? formatDateToOnlyTime(value, true) : '--'}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'vehicleRego',
      headerName: 'Vehicle',
      width: 150,
      filterable: false,
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value || ''}>
            <span>{value || `${row.unlistedVehicleRego} (Not listed)`}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'userName',
      headerName: 'Driver',
      flex: 1,
      filterable: false,
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value || row.userEmail || ''}>
            <span>{value || row.userEmail}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'maxCapacityInKgs',
      headerName: 'Max Capacity In Kgs',
      flex: 1,
      filterable: false,
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={row?.vehicle?.maxCapacityInKgs || ''}>
            <span>{row?.vehicle?.maxCapacityInKgs || ''}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'nextShiftVehicleDueByLocal',
      headerName: 'Next Shift Due',
      flex: 1,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Next shift vehicle due by time (local)">
          <span style={{ fontWeight: '600' }}>Next Shift Due</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || 'Not set'}>
            <span>
              {value ? formatDateToOnlyTime(value, true) : '--'}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'previousShiftDriverPhone',
      headerName: 'Previous Driver Phone',
      width: 180,
      filterable: false,
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={value || 'No phone number'}>
            <span>{value || '--'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'nextShiftDriverName',
      headerName: 'Next Driver',
      flex: 1,
      filterable: false,
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={value || 'No next driver assigned'}>
            <span>{value || '--'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'notifyDriver',
      headerName: 'Ask Return',
      width: 120,
      filterable: false,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <Tooltip title="Ask Driver to Update Return Time" arrow>
            <IconButton
              color="primary"
              aria-label="ask driver"
              onClick={() => notifyDriverToUpdateReturnTime(row.id)}
              size="small"
            >
              <NotificationsIcon />
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  const onSearchTextChange = (event: any) => {
    filterByText(event?.target?.value);
  };

  const onWarningSelectChange = (event: any) => {
    filterByWarning(event.target.value);
  };

  const refresh = () => {
    fetchAllRows();
  };

  useEffect(() => {
    fetchAllRows();
  }, []);

  const renderEachMobileCard = (row: FormattedShift) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        avatar={<TruckIcon />}
        title={row.vehicleRego || `${row.unlistedVehicleRego} (Not listed)`}
        subheader={`Driver: ${row.userName || row.userEmail}`}
      />
      <Divider />
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Return:</b> {row.scheduledEndUTC ? formatDateToLongDate(row.scheduledEndUTC, true) : '--'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Est. Return (User):</b> {row.estimatedVehicleReturnUtcRequestedByUser ? formatDateToOnlyTime(row.estimatedVehicleReturnUtcRequestedByUser, false) : '--'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Next Shift Due:</b> {row.nextShiftVehicleDueByLocal ? formatDateToOnlyTime(row.nextShiftVehicleDueByLocal, true) : '--'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Max Capacity In Kgs:</b> {row.vehicle?.maxCapacityInKgs || '--'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Previous Driver Phone:</b> {row.previousShiftDriverPhone || '--'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Next Driver:</b> {row.nextShiftDriverName || '--'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Vehicle Returns | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          <Card>
            <CardHeader
              action={
                (
                  <>
                    <IconButton onClick={refresh}>
                      <RefreshIcon />
                    </IconButton>
                  </>
                )
              }
              title="Vehicle Returns"
            />
            <Divider />
            <CardContent>
              <Hidden mdDown>
                <Toolbar style={{ justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <MultiRowActionButton
                      selectedRowsCount={selectedShifts?.length}
                      options={[
                        {
                          label: 'Request Truck Return Time',
                          onClick: notifySelectedDriversToUpdateReturnTime
                        }
                      ]}
                      disabled={!selectedShifts?.length}
                    />
                  </div>
                  <GlobalSearch
                    searchedText={searchedText}
                    onSearchTextChange={onSearchTextChange}
                    showFilterByColumn
                    columns={columns}
                    filterByColumn={filterByCol}
                    onFilterByColumnChange={onChangeFilterByColumn}
                    width="400px"
                  />
                </Toolbar>
                <Table
                  rows={visibleRows}
                  columns={columns}
                  totalRows={totalRowCount}
                  page={currentPage}
                  onPageChange={onPageChange}
                  sortModel={sortModel}
                  onSortModelChange={onSort}
                  checkboxSelection
                  selectionModel={selectedShifts}
                  onSelectionModelChange={handleSelectionModelChange}
                />
              </Hidden>
              <Hidden mdUp>
                <Toolbar sx={{ flexDirection: 'column' }}>
                  <div style={{ width: '100%', padding: '5px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Checkbox
                      checked={selectedShifts.length > 0 && selectedShifts.length === visibleRows.length}
                      indeterminate={selectedShifts.length > 0 && selectedShifts.length < visibleRows.length}
                      onChange={(e) => handleSelectAllShifts(e.target.checked)}
                      inputProps={{ 'aria-label': 'select all shifts' }}
                    />
                    {selectedShifts.length > 0 && (
                      <ConfirmButton
                        buttonLabel="Request Truck Return Time"
                        buttonIcon={<NotifyIcon />}
                        dialogTitle="Request Truck Return Time"
                        showCancelButton
                        message="You are about to request the driver to update truck return time."
                        onConfirm={notifySelectedDriversToUpdateReturnTime}
                        buttonColor="primary"
                        buttonSize="small"
                      />
                    )}
                  </div>
                  <div style={{ width: '100%', padding: '5px 0' }}>
                    <GlobalSearch
                      searchedText={searchedText}
                      onSearchTextChange={onSearchTextChange}
                      showFilterByColumn
                      columns={columns}
                      filterByColumn={filterByCol}
                      onFilterByColumnChange={onChangeFilterByColumn}
                    />
                  </div>
                </Toolbar>
                <MobileDataList
                  rows={visibleRows}
                  renderEachRow={renderEachMobileCard}
                  totalRows={totalRowCount}
                  page={currentPage}
                  onPageChange={onPageChange}
                />
              </Hidden>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  visibleRows: [
    ...state.shift?.scheduled?.filteredRows,
    ...state.shift?.ongoing?.filteredRows
  ].sort((a, b) => moment(a.scheduledEndUTC).diff(moment(b.scheduledEndUTC))),
  totalRowCount: [
    ...state.shift?.scheduled?.filteredRows,
    ...state.shift?.ongoing?.filteredRows
  ].length,
  currentPage: state.shift?.ongoing?.currentPage,
  searchedText: state.shift?.ongoing?.searchedText,
  selectedWarning: state.shift?.ongoing?.filterBy,
  sortModel: state.shift?.ongoing?.sortModel,
  filterByCol: state.shift?.ongoing?.filterByCol
});

const mapDispatchToProps = (dispatch: any) => ({
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts()),
  fetchAllRows: () =>
    dispatch(
      refreshDataWithDateRange(
        moment().startOf('day').toLocaleString(),
        moment().endOf('day').toLocaleString()
      )
    ),
  filterByText: (text: string) => dispatch(searchByTextOngoing(text)),
  filterByWarning: (text: string) => dispatch(searchByWarningOngoing(text)),
  onPageChange: (page: any) => dispatch(changePageOngoing(page)),
  onSort: (sortModel: any) => dispatch(changeSortModelOngoing(sortModel)),
  onChangeFilterByColumn: (col: any) =>
    dispatch(changeFilterByColumnOngoing(col))
});

export default connect(mapStateToProps, mapDispatchToProps)(VehiclesReturns);
